import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, Globe, FileText, Shield, Clock } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

export default function Impressum({ darkMode = false }) {
  const { t } = useTranslation();

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const Section = ({ title, children }) => (
    <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <h2 style={{ fontSize: 14, fontWeight: 800, color: textPrimary, marginBottom: 12 }}>{title}</h2>
      {children}
    </div>
  );

  const Row = ({ icon: Icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
      <Icon size={14} color={accent} style={{ flexShrink: 0, marginTop: 2 }} />
      <span style={{ fontSize: 13, color: textSecondary, lineHeight: 1.6 }}>{children}</span>
    </div>
  );

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ position: 'relative', paddingTop: 'clamp(90px, 13vh, 120px)', paddingBottom: 48, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.12) 0%, transparent 65%)' : 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.07) 0%, transparent 65%)' }} />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 12 }}>Rechtliches</div>
          <h1 style={{ fontSize: 'clamp(24px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 10 }}>
            {t('impressum.title')}
          </h1>
          <p style={{ fontSize: 14, color: textSecondary, lineHeight: 1.65 }}>{t('impressum.subtitle')}</p>
        </div>
      </section>

      {/* Content */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px 80px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Info cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <Section title={t('impressum.companyInfoTitle')}>
            <Row icon={Globe}><strong>Dustin Derr GbR</strong></Row>
            <Row icon={MapPin}>Alte Molkerei 4<br />49692 Cappeln (Oldenburg)<br />Deutschland</Row>
            <Row icon={Phone}>+49 174 1628004</Row>
            <Row icon={Mail}>info@derrdigital.de</Row>
          </Section>

          <Section title={t('impressum.legalRepTitle')}>
            <Row icon={FileText}><strong>{t('impressum.managementLabel')}</strong><br />Dustin Derr</Row>
          </Section>

          <Section title={t('impressum.responsibilityTitle')}>
            <Row icon={Shield}><strong>{t('impressum.contentResponsibleLabel')}</strong><br />Dustin Derr GbR</Row>
            <p style={{ fontSize: 12, color: textSecondary, lineHeight: 1.6, marginTop: 8 }}>{t('impressum.addressNote')}</p>
          </Section>
        </div>

        {/* Legal text sections */}
        {[
          { title: t('impressum.disputeTitle'), content: t('impressum.disputeText'), hasLink: true },
          { title: t('impressum.liabilityTitle'), content: t('impressum.liabilityText') },
          { title: t('impressum.linksTitle'), content: t('impressum.linksText') },
          { title: t('impressum.copyrightTitle'), content: t('impressum.copyrightText') },
        ].map(({ title, content, hasLink }) => (
          <div key={title} style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '20px 22px' }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>{title}</h2>
            <div style={{ fontSize: 13, color: textSecondary, lineHeight: 1.7 }}>
              {content.split('\n\n').map((para, i) => (
                <p key={i} style={{ marginBottom: i < content.split('\n\n').length - 1 ? 10 : 0 }}>
                  {hasLink && para.includes('https://') ? (
                    <>
                      {para.split('https://ec.europa.eu/consumers/odr/')[0]}
                      <a href="https://ec.europa.eu/consumers/odr/" style={{ color: accent, textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        https://ec.europa.eu/consumers/odr/
                      </a>
                      {para.split('https://ec.europa.eu/consumers/odr/')[1]}
                    </>
                  ) : para}
                </p>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: textSecondary }}>
          <Clock size={12} color={accent} />
          {t('impressum.lastUpdated')}
        </div>
      </div>
    </div>
  );
}
